import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { EvIoGameSetupSettings, MatchStatus, PlatformType } from '@okletsplay/core';
import { MatchModalCountdown } from 'modules/matches/components/MatchModalCountdown';
import { currentMatchSelector } from 'modules/matches/store/current-match/selectors';
import moment from 'moment';
import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ChessMatchTypeFormat } from 'shared/components/Format/ChessMatchTypeFormat';
import { GameCurrencyWithFee } from 'modules/game-payments/components';
import {
  EvIoMatchTypeFormat,
  EvIoMatchTypeFormatVariant,
} from 'shared/components/Format/EvIoMatchTypeFormat';
import { useStyles } from './MatchDetailsModalHeader.styles';

interface MatchDetailsModalHeaderProps {
  title: string;
  logo: string;
  entryFee: number;
  startedAt?: string;
  status: MatchStatus;
}

export const MatchDetailsModalHeader: FC<MatchDetailsModalHeaderProps> = ({
  logo,
  title,
  entryFee,
  startedAt,
  status,
}) => {
  const theme = useTheme();
  const { classes } = useStyles();
  const isFullScreen = useMediaQuery(theme.breakpoints.up('lg'));

  const match = useSelector(currentMatchSelector.match);
  const expirationTime = useSelector(currentMatchSelector.expirationTime);
  const timeSettings = useSelector(currentMatchSelector.timeSettings);

  const platformType = match?.game?.platformType;

  const expirationDate = useMemo(() => {
    if (!startedAt) {
      return '';
    }

    return moment(startedAt).add(expirationTime, 'seconds').toISOString();
  }, [startedAt, expirationTime]);

  const timeTooltip = useMemo(() => {
    if (status === MatchStatus.Lobby) {
      return 'All players have to be ready in-game in';
    }

    if (status === MatchStatus.Live) {
      return 'The match have to be completed in';
    }

    return '';
  }, [status]);

  const gameSettings = useMemo(() => {
    const defaultSettings = 'Standard • 1v1';

    if (timeSettings) {
      return (
        <>
          {defaultSettings}{' '}
          <ChessMatchTypeFormat
            limit={timeSettings.clockLimit}
            increment={timeSettings.clockIncrement}
          />
        </>
      );
    }

    if (platformType === PlatformType.EvIo) {
      return (
        <EvIoMatchTypeFormat
          variant={EvIoMatchTypeFormatVariant.OneLine}
          settings={match.matchSetupSettings as unknown as EvIoGameSetupSettings}
          gameId={match.game?.id}
        />
      );
    }

    return defaultSettings;
  }, [match.game?.id, match.matchSetupSettings, platformType, timeSettings]);

  return (
    <Box className={classes.root}>
      <Box className={classes.gameDetails}>
        <Box className={classes.logoContainer}>
          <img className={classes.logo} src={logo} alt="game-logo" />
        </Box>
        <Box className={classes.descriptionContainer}>
          <Typography className={classes.title}>{title}</Typography>
          <Typography className={classes.description}>
            {gameSettings} <GameCurrencyWithFee amount={entryFee} />
          </Typography>
          {expirationDate && !isFullScreen && (
            <MatchModalCountdown date={expirationDate} prefix="ends in" tooltip={timeTooltip} />
          )}
        </Box>
      </Box>
      {expirationDate && isFullScreen && (
        <Box flex={1}>
          <MatchModalCountdown date={expirationDate} prefix="ends in" tooltip={timeTooltip} />
        </Box>
      )}
    </Box>
  );
};
